import {
  ArrowDown,
  ArrowUp,
  Bookmark,
  BookmarkPlus,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  FileJson,
  FileSpreadsheet,
  Filter,
  Loader2,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, getMatchLevelBadgeVariant, getStatusBadgeVariant } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card, CardContent } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { useFilterOptions } from "../hooks/useFilterOptions";
import { useFilterPresets } from "../hooks/useFilterPresets";
import { useSkillCheckFilters } from "../hooks/useSkillCheckFilters";
import { apiService } from "../services/api.service";
import type { SkillCheckAdminItem, SkillCheckJobStatus } from "../types/admin.types";
import { downloadCSV, downloadJSON, estimateExportSize, formatFileSize } from "../utils/export";

const PAGE_SIZE = 20;

const statusOptions: { value: SkillCheckJobStatus | ""; label: string }[] = [
  { value: "", label: "すべてのステータス" },
  { value: "COMPLETED", label: "完了" },
  { value: "FAILED", label: "失敗" },
  { value: "PROCESSING", label: "処理中" },
  { value: "PENDING", label: "待機中" },
  { value: "RETRYABLE", label: "リトライ可" },
];

const matchLevelOptions = [
  { value: -1, label: "すべてのレベル" },
  { value: 5, label: "Lv.5" },
  { value: 4, label: "Lv.4" },
  { value: 3, label: "Lv.3" },
  { value: 2, label: "Lv.2" },
  { value: 1, label: "Lv.1" },
  { value: 0, label: "Lv.0" },
];

export function SkillChecksPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<SkillCheckAdminItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // URLクエリパラメータと同期するフィルタ
  const {
    filters,
    apiFilters,
    searchInput,
    setSearchInput,
    setStatus,
    setMatchLevel,
    setTeamId,
    setCompanyId,
    setJobPostingId,
    setSince,
    setUntil,
    setSortOrder,
    setPage,
    resetFilters,
    activeFilterCount,
    getShareableUrl,
    updateFilters,
  } = useSkillCheckFilters();

  // フィルタプリセット
  const { presets, savePreset, deletePreset, canSaveMore } = useFilterPresets();

  // プリセット保存モーダル
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false);

  // URLコピー成功通知
  const [urlCopied, setUrlCopied] = useState(false);

  // エクスポート関連
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // フィルターオプションを取得
  const { data: filterOptions, isLoading: isFilterOptionsLoading } = useFilterOptions();

  // 会社でフィルタしたチーム一覧（チーム名に会社名を付加）
  const filteredTeams = useMemo(() => {
    if (!filterOptions) {
      return [];
    }
    const teams = filters.companyId
      ? filterOptions.teams.filter((t) => t.companyId === filters.companyId)
      : filterOptions.teams;

    // 会社フィルタがない場合は、チーム名に会社名を付加して表示
    if (!filters.companyId) {
      return teams.map((team) => {
        const company = filterOptions.companies.find((c) => c.id === team.companyId);
        return {
          ...team,
          displayName: company ? `${team.name} (${company.name})` : team.name,
        };
      });
    }
    return teams.map((team) => ({ ...team, displayName: team.name }));
  }, [filters.companyId, filterOptions]);

  // チーム/会社でフィルタした求人一覧
  const filteredJobPostings = useMemo(() => {
    if (!filterOptions) {
      return [];
    }
    let jobPostings = filterOptions.jobPostings;

    // チームでフィルタ
    if (filters.teamId) {
      jobPostings = jobPostings.filter((jp) => jp.teamId === filters.teamId);
    } else if (filters.companyId) {
      // 会社でフィルタ（会社に属するチームの求人）
      const companyTeamIds = filterOptions.teams
        .filter((t) => t.companyId === filters.companyId)
        .map((t) => t.id);
      jobPostings = jobPostings.filter((jp) => companyTeamIds.includes(jp.teamId));
    }

    return jobPostings;
  }, [filters.teamId, filters.companyId, filterOptions]);

  // 会社フィルタが変更されたらチームフィルタと求人フィルタをリセット
  useEffect(() => {
    if (filters.companyId && filters.teamId) {
      const team = filterOptions?.teams.find((t) => t.id === filters.teamId);
      if (team && team.companyId !== filters.companyId) {
        setTeamId("");
        setJobPostingId("");
      }
    }
  }, [filters.companyId, filters.teamId, filterOptions?.teams, setTeamId, setJobPostingId]);

  // チームフィルタが変更されたら求人フィルタをリセット
  useEffect(() => {
    if (filters.teamId && filters.jobPostingId) {
      const jobPosting = filterOptions?.jobPostings.find((jp) => jp.id === filters.jobPostingId);
      if (jobPosting && jobPosting.teamId !== filters.teamId) {
        setJobPostingId("");
      }
    }
  }, [filters.teamId, filters.jobPostingId, filterOptions?.jobPostings, setJobPostingId]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.listSkillChecks(
        apiFilters,
        PAGE_SIZE,
        filters.page * PAGE_SIZE,
      );
      setItems(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "データの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, [apiFilters, filters.page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // URLをクリップボードにコピー
  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getShareableUrl());
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.warn("Failed to copy URL:", err);
    }
  }, [getShareableUrl]);

  // プリセットを保存
  const handleSavePreset = useCallback(() => {
    if (presetName.trim()) {
      const { page: _, ...filtersWithoutPage } = filters;
      savePreset(presetName.trim(), filtersWithoutPage);
      setPresetName("");
      setIsPresetModalOpen(false);
    }
  }, [presetName, filters, savePreset]);

  // プリセットを適用
  const handleApplyPreset = useCallback(
    (preset: (typeof presets)[0]) => {
      updateFilters({ ...preset.filters, page: 0 });
      setSearchInput(preset.filters.query);
      setShowPresetsDropdown(false);
    },
    [updateFilters, setSearchInput],
  );

  // 全データを取得してエクスポート
  const fetchAllDataForExport = useCallback(async (): Promise<SkillCheckAdminItem[]> => {
    // 一度に最大取得件数（APIの制限に合わせて調整）
    const batchSize = 100;
    const allItems: SkillCheckAdminItem[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await apiService.listSkillChecks(apiFilters, batchSize, offset);
      allItems.push(...response.items);
      hasMore = response.hasMore;
      offset += batchSize;
    }

    return allItems;
  }, [apiFilters]);

  // CSVエクスポート
  const handleExportCSV = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportError(null);
      setShowExportDropdown(false);

      // 全データを取得
      const allItems = await fetchAllDataForExport();

      if (allItems.length === 0) {
        setExportError("エクスポートするデータがありません");
        return;
      }

      // CSVダウンロード
      downloadCSV(allItems);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "エクスポートに失敗しました");
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport]);

  // JSONエクスポート
  const handleExportJSON = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportError(null);
      setShowExportDropdown(false);

      // 全データを取得
      const allItems = await fetchAllDataForExport();

      if (allItems.length === 0) {
        setExportError("エクスポートするデータがありません");
        return;
      }

      // JSONダウンロード（フィルタ条件も含める）
      downloadJSON(allItems, apiFilters);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "エクスポートに失敗しました");
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport, apiFilters]);

  // エクスポートファイルサイズの推定
  const estimatedCSVSize = formatFileSize(
    estimateExportSize(items, "csv") * (total / items.length || 1),
  );
  const estimatedJSONSize = formatFileSize(
    estimateExportSize(items, "json") * (total / items.length || 1),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-bold text-2xl text-gray-900">スキルチェック一覧</h1>
        <div className="flex items-center gap-4">
          <p className="text-gray-500 text-sm">全 {total} 件</p>

          {/* URLコピーボタン */}
          <Button
            className="relative"
            onClick={handleCopyUrl}
            size="sm"
            title="フィルタ付きURLをコピー"
            variant="secondary"
          >
            <Copy className="mr-1 h-4 w-4" />
            URLをコピー
            {urlCopied && (
              <span className="-top-8 -translate-x-1/2 absolute left-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-white text-xs">
                コピーしました
              </span>
            )}
          </Button>

          {/* エクスポートボタン */}
          <div className="relative">
            <Button
              disabled={isExporting || total === 0}
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              size="sm"
              title={total === 0 ? "エクスポートするデータがありません" : "データをエクスポート"}
              variant="primary"
            >
              {isExporting ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1 h-4 w-4" />
              )}
              {isExporting ? "エクスポート中..." : "エクスポート"}
            </Button>

            {showExportDropdown && !isExporting && (
              <>
                {/* オーバーレイ */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowExportDropdown(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowExportDropdown(false);
                    }
                  }}
                />
                {/* ドロップダウンメニュー */}
                <div className="absolute top-full right-0 z-20 mt-1 min-w-[200px] rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="p-2">
                    <p className="mb-2 px-2 text-gray-500 text-xs">{total} 件をエクスポート</p>
                    <button
                      className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-gray-50"
                      onClick={handleExportCSV}
                      type="button"
                    >
                      <FileSpreadsheet className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">CSV形式</p>
                        <p className="text-gray-500 text-xs">推定サイズ: {estimatedCSVSize}</p>
                      </div>
                    </button>
                    <button
                      className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-gray-50"
                      onClick={handleExportJSON}
                      type="button"
                    >
                      <FileJson className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">JSON形式</p>
                        <p className="text-gray-500 text-xs">推定サイズ: {estimatedJSONSize}</p>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* エクスポートエラー表示 */}
      {exportError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {exportError}
          <button
            className="ml-4 text-red-500 underline hover:text-red-700"
            onClick={() => setExportError(null)}
            type="button"
          >
            閉じる
          </button>
        </div>
      )}

      {/* フィルタ */}
      <Card>
        <CardContent className="py-4">
          <div className="space-y-4">
            {/* 1行目: 検索とステータス、マッチレベル */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 text-sm">フィルタ:</span>
              </div>

              <div className="min-w-[200px] flex-1">
                <div className="relative">
                  <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-gray-400" />
                  <input
                    className="w-full rounded-md border border-gray-300 py-1.5 pr-4 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="候補者名、求人タイトルで検索..."
                    type="text"
                    value={searchInput}
                  />
                </div>
              </div>

              <select
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setStatus(e.target.value as SkillCheckJobStatus | "")}
                value={filters.status}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setMatchLevel(Number(e.target.value))}
                value={filters.matchLevel}
              >
                {matchLevelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 2行目: 会社、チーム、日付範囲 */}
            <div className="flex flex-wrap items-center gap-4">
              <select
                className="min-w-[180px] rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isFilterOptionsLoading}
                onChange={(e) => setCompanyId(e.target.value)}
                value={filters.companyId}
              >
                <option value="">すべての会社</option>
                {filterOptions?.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>

              <select
                className="min-w-[180px] rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isFilterOptionsLoading}
                onChange={(e) => setTeamId(e.target.value)}
                value={filters.teamId}
              >
                <option value="">すべてのチーム</option>
                {filteredTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.displayName}
                  </option>
                ))}
              </select>

              <select
                className="min-w-[200px] rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isFilterOptionsLoading || filteredJobPostings.length === 0}
                onChange={(e) => setJobPostingId(e.target.value)}
                title={filteredJobPostings.length === 0 ? "求人がありません" : "求人でフィルタ"}
                value={filters.jobPostingId}
              >
                <option value="">すべての求人</option>
                {filteredJobPostings.map((jp) => (
                  <option key={jp.id} value={jp.id}>
                    {jp.title.length > 30 ? `${jp.title.slice(0, 30)}...` : jp.title}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setSince(e.target.value)}
                  type="date"
                  value={filters.since}
                />
                <span className="text-gray-500">〜</span>
                <input
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setUntil(e.target.value)}
                  type="date"
                  value={filters.until}
                />
              </div>

              {/* フィルタリセットボタン */}
              {activeFilterCount > 0 && (
                <Button onClick={resetFilters} size="sm" variant="secondary">
                  <X className="mr-1 h-4 w-4" />
                  フィルタをクリア ({activeFilterCount})
                </Button>
              )}
            </div>

            {/* 3行目: プリセット管理 */}
            <div className="flex flex-wrap items-center gap-4 border-gray-200 border-t pt-4">
              {/* プリセット保存ボタン */}
              <Button
                disabled={!canSaveMore || activeFilterCount === 0}
                onClick={() => setIsPresetModalOpen(true)}
                size="sm"
                title={
                  canSaveMore
                    ? activeFilterCount === 0
                      ? "フィルタを設定してから保存してください"
                      : "現在のフィルタをプリセットとして保存"
                    : "プリセットは最大10件まで保存できます"
                }
                variant="secondary"
              >
                <BookmarkPlus className="mr-1 h-4 w-4" />
                プリセットを保存
              </Button>

              {/* プリセット選択ドロップダウン */}
              <div className="relative">
                <Button
                  disabled={presets.length === 0}
                  onClick={() => setShowPresetsDropdown(!showPresetsDropdown)}
                  size="sm"
                  variant="secondary"
                >
                  <Bookmark className="mr-1 h-4 w-4" />
                  保存済みプリセット ({presets.length})
                </Button>

                {showPresetsDropdown && presets.length > 0 && (
                  <>
                    {/* オーバーレイ */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowPresetsDropdown(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setShowPresetsDropdown(false);
                        }
                      }}
                    />
                    {/* ドロップダウンメニュー */}
                    <div className="absolute top-full right-0 z-20 mt-1 min-w-[250px] rounded-md border border-gray-200 bg-white shadow-lg">
                      <div className="max-h-[300px] overflow-y-auto p-2">
                        {presets.map((preset) => (
                          <div
                            className="group flex items-center justify-between rounded-md p-2 hover:bg-gray-50"
                            key={preset.id}
                          >
                            <button
                              className="flex-1 text-left text-sm"
                              onClick={() => handleApplyPreset(preset)}
                              type="button"
                            >
                              <p className="font-medium text-gray-900">{preset.name}</p>
                              <p className="text-gray-500 text-xs">
                                {new Date(preset.createdAt).toLocaleDateString("ja-JP")}
                              </p>
                            </button>
                            <button
                              className="p-1 text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                              onClick={() => deletePreset(preset.id)}
                              title="削除"
                              type="button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* プリセット保存モーダル */}
      {isPresetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 font-semibold text-lg">フィルタプリセットを保存</h2>
            <input
              autoFocus
              className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSavePreset();
                }
              }}
              placeholder="プリセット名を入力..."
              type="text"
              value={presetName}
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setPresetName("");
                  setIsPresetModalOpen(false);
                }}
                variant="secondary"
              >
                キャンセル
              </Button>
              <Button disabled={!presetName.trim()} onClick={handleSavePreset} variant="primary">
                保存
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
      )}

      {/* テーブル */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ステータス</TableHead>
              <TableHead>候補者</TableHead>
              <TableHead>求人</TableHead>
              <TableHead>チーム / 会社</TableHead>
              <TableHead>マッチレベル</TableHead>
              <TableHead>
                <button
                  className="flex items-center gap-1 hover:text-gray-900"
                  onClick={() => setSortOrder(filters.sortOrder === "desc" ? "asc" : "desc")}
                  type="button"
                >
                  作成日時
                  {filters.sortOrder === "desc" ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUp className="h-4 w-4" />
                  )}
                </button>
              </TableHead>
              <TableHead>処理時間</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell className="py-8 text-center" colSpan={7}>
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                    <span className="ml-2 text-gray-500">読み込み中...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell className="py-8 text-center text-gray-500" colSpan={7}>
                  データがありません
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  className="cursor-pointer hover:bg-gray-50"
                  key={item.id}
                  onClick={() => navigate(`/admin/skill-checks/${item.id}`)}
                >
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(item.status)}>{item.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.candidateName ?? "名前未取得"}</p>
                      <p className="text-gray-500 text-xs">{item.candidateSource ?? "-"}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="max-w-xs truncate" title={item.jobPostingTitle}>
                      {item.jobPostingTitle}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{item.teamName}</p>
                      <p className="text-gray-500 text-xs">{item.companyName}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.overallMatchLevel !== null ? (
                      <Badge variant={getMatchLevelBadgeVariant(item.overallMatchLevel)}>
                        Lv.{item.overallMatchLevel}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{new Date(item.createdAt).toLocaleString("ja-JP")}</p>
                  </TableCell>
                  <TableCell>
                    {item.completedAt && item.startedAt ? (
                      <p className="text-sm">
                        {(
                          (new Date(item.completedAt).getTime() -
                            new Date(item.startedAt).getTime()) /
                          1000
                        ).toFixed(1)}
                        s
                      </p>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* ページネーション */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-gray-200 border-t px-6 py-4">
            <p className="text-gray-500 text-sm">
              {filters.page * PAGE_SIZE + 1} - {Math.min((filters.page + 1) * PAGE_SIZE, total)} /{" "}
              {total} 件
            </p>
            <div className="flex items-center gap-2">
              <Button
                disabled={filters.page === 0}
                onClick={() => setPage(Math.max(0, filters.page - 1))}
                size="sm"
                variant="secondary"
              >
                <ChevronLeft className="h-4 w-4" />
                前へ
              </Button>
              <span className="text-gray-600 text-sm">
                {filters.page + 1} / {totalPages}
              </span>
              <Button
                disabled={filters.page >= totalPages - 1}
                onClick={() => setPage(Math.min(totalPages - 1, filters.page + 1))}
                size="sm"
                variant="secondary"
              >
                次へ
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
